/**
 * Блок про развёртки стен — главное отличие от «просто планировщика».
 * Справа развёртка той же стены, что на плане в hero: проёмы, розетка, выключатель,
 * спуски кабеля и размерные цепочки по ширине и высоте. Рисуется, когда блок заехал в экран.
 */
import "./landing.css";
import { UIKit } from "@/shared/ui";
import { ELEVATION, elevationChain, elevationHeights } from "../logic";
import { DimensionLine } from "./DimensionLine";

/** Масштаб рисунка: единиц viewBox на миллиметр. */
const SCALE = 0.085;
const LEFT = 84;
const FLOOR = 282;
const x = (mm: number) => LEFT + mm * SCALE;
const y = (mm: number) => FLOOR - mm * SCALE;
const at = (seconds: number) => ({ "--d": `${seconds}s` }) as React.CSSProperties;
const mm = (value: number) => value.toLocaleString("ru-RU");

const POINTS = [
	"Каждая стена каждой комнаты - ширина в чистоте, без ручного пересчёта",
	"Розетки, выключатели и спуски кабеля с высотами установки",
	"Размерные цепочки с засечками, как у проектировщика",
	"Подвинули стену на плане - развёртка уже другая",
];

/** Пример развёртки стены с проёмами и размерными цепочками. */
export const ElevationArt = () => {
	const chain = elevationChain();
	const heights = elevationHeights();
	const ceiling = y(ELEVATION.height);

	return (
		<svg
			viewBox="0 0 640 350"
			role="img"
			aria-label="Развёртка стены гостиной с размерами"
			className="h-auto w-full"
		>
			<text
				className="lp-fade lp-mono"
				style={at(0)}
				x={LEFT}
				y="24"
				fontSize="10"
				fill="var(--color-ink)"
			>
				{ELEVATION.title.toUpperCase()}
			</text>
			<text
				className="lp-fade lp-mono"
				style={at(0)}
				x={x(ELEVATION.width)}
				y="24"
				fontSize="10"
				fill="#77839a"
				textAnchor="end"
			>
				М 1:50
			</text>

			{/* стена: контур, пол толще */}
			<rect
				className="lp-draw lp-ink"
				x={LEFT}
				y={ceiling}
				width={ELEVATION.width * SCALE}
				height={ELEVATION.height * SCALE}
				pathLength={1}
				style={at(0.1)}
			/>
			<path
				className="lp-draw"
				d={`M${LEFT - 10} ${FLOOR}H${x(ELEVATION.width) + 10}`}
				stroke="var(--color-ink)"
				strokeWidth="2.4"
				pathLength={1}
				style={at(0.1)}
			/>

			{ELEVATION.openings.map((opening, i) => {
				const left = x(opening.from);
				const right = x(opening.to);
				const top = y(opening.height);
				return (
					<g key={opening.from}>
						<path
							className="lp-draw lp-ink"
							d={`M${left} ${FLOOR}V${top}H${right}V${FLOOR}`}
							fill="#f6f8fb"
							pathLength={1}
							style={at(0.6 + i * 0.15)}
						/>
						<path
							className="lp-draw lp-ink-thin"
							d={
								opening.kind === "arch"
									? `M${left} ${top}L${right} ${FLOOR}M${right} ${top}L${left} ${FLOOR}`
									: `M${left} ${top}L${right} ${(top + FLOOR) / 2}L${left} ${FLOOR}`
							}
							strokeDasharray={opening.kind === "door" ? "4 3" : undefined}
							pathLength={1}
							style={at(0.9 + i * 0.15)}
						/>
						<text
							className="lp-fade lp-svg-text"
							x={(left + right) / 2}
							y={top - 6}
							style={at(1.1)}
						>
							{opening.kind === "arch" ? "АРКА" : "ДВЕРЬ"} {mm(opening.height)}
						</text>
					</g>
				);
			})}

			{ELEVATION.points.map((point, i) => {
				const cx = x(point.at);
				const cy = y(point.height);
				return (
					<g key={point.at}>
						<path
							className="lp-draw lp-ink-accent"
							d={`M${cx} ${ceiling}V${cy - 6}`}
							strokeDasharray="1"
							pathLength={1}
							style={at(1.3 + i * 0.15)}
						/>
						<g className="lp-pop" style={at(1.6 + i * 0.15)}>
							<rect
								x={cx - 6}
								y={cy - 6}
								width="12"
								height="12"
								rx="2"
								fill="#f7f5f0"
								stroke="#879b83"
								strokeWidth="1.4"
							/>
							{point.kind === "socket" ? (
								<circle cx={cx} cy={cy} r="3" fill="none" stroke="#879b83" strokeWidth="1.2" />
							) : (
								<path
									d={`M${cx - 2} ${cy + 3}L${cx + 2} ${cy - 3}`}
									stroke="#879b83"
									strokeWidth="1.4"
								/>
							)}
							<text
								className="lp-svg-text"
								x={cx + 10}
								y={cy + 3}
								style={{ textAnchor: "start" }}
								fill="#879b83"
							>
								h {mm(point.height)}
							</text>
						</g>
					</g>
				);
			})}

			{/* цепочка по ширине и габарит под ней */}
			{chain.slice(1).map((mark, i) => (
				<DimensionLine
					key={mark}
					from={x(chain[i])}
					to={x(mark)}
					at={FLOOR + 22}
					base={FLOOR + 4}
					label={mm(mark - chain[i])}
					delay={1.8 + i * 0.06}
				/>
			))}
			<DimensionLine
				from={LEFT}
				to={x(ELEVATION.width)}
				at={FLOOR + 50}
				base={FLOOR + 26}
				label={mm(ELEVATION.width)}
				delay={2.3}
			/>
			{/* цепочка по высоте */}
			{heights.slice(1).map((mark, i) => (
				<DimensionLine
					key={mark}
					vertical
					from={y(mark)}
					to={y(heights[i])}
					at={LEFT - 26}
					base={LEFT - 4}
					label={mm(mark - heights[i])}
					delay={2 + i * 0.06}
				/>
			))}
		</svg>
	);
};

export const ElevationSection = () => (
	<section aria-labelledby="lp-elev-title" className="border-y border-border bg-surface">
		<div
			data-reveal
			className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-8 lg:grid-cols-landing-elevation lg:py-28"
		>
			<div>
				<UIKit.Text as="span" className="lp-rise lp-mono block text-xs text-muted">
					Развёртки стен
				</UIKit.Text>
				<h2
					id="lp-elev-title"
					className="lp-rise lp-display mt-4 text-landing-section-heading"
					style={at(0.08)}
				>
					Каждая стена -{" "}
					<UIKit.Text as="span" className="lp-outline">
						отдельным
					</UIKit.Text>{" "}
					листом
				</h2>
				<UIKit.Text
					className="lp-rise mt-6 max-w-120 text-base leading-relaxed text-muted"
					style={at(0.16)}
				>
					Электрику и отделку на объекте делают по развёрткам. Планировщик строит их из плана сам -
					рисовать ничего не нужно.
				</UIKit.Text>
				<ul className="mt-8 grid gap-3">
					{POINTS.map((point, i) => (
						<li
							key={point}
							className="lp-rise flex gap-3 text-sm leading-copy-loose"
							style={at(0.24 + i * 0.06)}
						>
							<UIKit.Text
								as="span"
								aria-hidden="true"
								className="mt-2 h-px w-4 shrink-0 bg-accent"
							/>
							{point}
						</li>
					))}
				</ul>
			</div>
			<div
				className="lp-rise rounded-2xl border border-border bg-slate-50 p-4 sm:p-6"
				style={at(0.1)}
			>
				<ElevationArt />
			</div>
		</div>
	</section>
);
